import React, { useCallback, useMemo } from 'react';
import { CardElement } from "@stripe/react-stripe-js";
import { css, Theme, useTheme } from "@emotion/react";
import { FieldList, OnFieldChange } from "core/hooks/useForm";
import { ReactComponent as CreditCardIcon } from "images/icons/credit-card.svg";
import SimpleCheckbox from "core/Components/Form/SimpleCheckbox";
import DetailsForm from "pages/Components/CartAndCheckout/components/DetailsForm";
import ContentCombine from "core/Components/ContentCombine";
import { NOOP } from "core/constants";

type DefaultState = {
    errorMessage: string | null,
    errorType: string | null,
    isVisibleErrorMessage: boolean,
}

type Props = {
    fields: FieldList,
    isInvoice: boolean,
    onFieldChange?: OnFieldChange
    errors?: FieldList,
    orderState: DefaultState,
    setOrderState: (state: DefaultState) => void,
    isUsingShippingAddress: boolean,
    onAddressChange: (value: boolean) => void,
    onAddressFinder: (isUsed: boolean) => void,
}

const Billing = (
    {
        fields,
        isInvoice = false,
        onFieldChange = NOOP,
        errors = {},
        orderState,
        setOrderState,
        isUsingShippingAddress = false,
        onAddressChange = NOOP,
        onAddressFinder = NOOP,
    }: Props
) => {
    const theme: Theme = useTheme();

    const cardElementOptions = useMemo(() => {
        return {
            style: {
                base: {
                    padding: '40px',
                    lineHeight: '20px',
                    fontSize: '16px',
                    fontSmoothing: 'antialiased',
                    fontFamily: theme.fonts.frutiger,
                    '::placeholder': {
                        color: theme.colours.grey[500],
                    },
                }
            }
        };
    }, [theme]);

    const cardElementErrorEffect = useCallback(() => {
        setOrderState({ errorMessage: '', isVisibleErrorMessage: false, errorType: 'card-error' });
    }, [setOrderState]);

    const isErrorDisplayed = orderState.isVisibleErrorMessage && orderState.errorType === 'card-error';

    const containerStyle = css`
        padding-top: 1px;
        margin-bottom: -10px;
    `;

    const checkoutFormBlock = css`
        padding: 17px 17px 5px 17px;
        background: white;
        box-shadow: ${theme.borderAndShadow.boxShadow3};
        border-radius: 17px;
        width: 621px;

        ${theme.breakpoints.down('lg')} {
            width: 100%;
        }
    `;

    const cardContainerStyle = css`
        padding-left: 5px;
        margin-top: -10px;
    `;

    const labelRowStyle = css`
        display: flex;
    `;

    const cardIconStyle = css`
        width: 35px;
        height: 35px;

        path {
            fill: ${theme.colours.curiousBlue2}
        }
    `;

    const cardLabelStyle = css`
        font-size: 18px;
        line-height: 26px;
        margin-left: 17px;

        ${theme.breakpoints.down('lg')} {
            font-size: 16px;
            line-height: 20px;
        }
    `;

    const checkoutForm = css`
        margin-top: 10px;
        padding: 14px;
        background: transparent;
        outline: none;
    `;

    const errorDivideStyle = ({ hasError = false }) => css`
        height: 3px;
        background: ${hasError ? 'red' : theme.colours.curiousBlue2};
        border-radius: 3px;
    `;

    const paymentValidationError = css`
        min-height: 20px;
        line-height: 15px;
        font-size: 12px;
        color: ${theme.colours.crimson};
        margin-top: 5px;
    `;

    const billingSelectionContainerStyle = css`
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;

        ${!isInvoice && css`
            margin-top: 32px;
        `}
    `;

    const addressLabelStyle = css`
        font-weight: ${theme.fonts.weights.bold};
        justify-content: start;
    `;

    const checkboxStyle = css`
        margin-top: 18px;
        width: fit-content;
    `;

    return (
        <div css={containerStyle}>
            {isInvoice ? <div /> : (
                <>
                    <div css={checkoutFormBlock}>
                        <div css={labelRowStyle}>
                            <CreditCardIcon css={cardIconStyle} />
                            <div css={cardLabelStyle}>Card detail</div>
                        </div>
                        <div css={cardContainerStyle}>
                            <div css={checkoutForm}>
                                <CardElement options={cardElementOptions} onChange={cardElementErrorEffect} />
                            </div>
                            <div css={errorDivideStyle({ hasError: isErrorDisplayed })} />
                            <div id={"errorMessage"} css={paymentValidationError}>
                                {isErrorDisplayed && <span>{orderState.errorMessage}</span>}
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div>
                <div css={billingSelectionContainerStyle}>
                    <ContentCombine fontSizeType={'18'} Tag={'p'} css={addressLabelStyle}>Billing
                        address</ContentCombine>
                    <SimpleCheckbox
                        key={'use-shipping-address'}
                        label={'Same as shipping address'}
                        name={'use-shipping-address'}
                        onChange={() => onAddressChange(true)}
                        value={isUsingShippingAddress}
                        css={checkboxStyle}
                    />
                    <SimpleCheckbox
                        key={'use-new-address'}
                        label={'Use a different billing address'}
                        name={'use-new-address'}
                        onChange={() => onAddressChange(false)}
                        value={!isUsingShippingAddress}
                        css={checkboxStyle}
                    />
                </div>
                <DetailsForm
                    fields={isUsingShippingAddress ? fields.fields : fields.billingFields}
                    errors={errors}
                    onFieldChange={onFieldChange}
                    useBilling={!isUsingShippingAddress}
                    hasAsterisk={!isUsingShippingAddress}
                    isReadOnly={isUsingShippingAddress}
                    onAddressFinder={onAddressFinder}
                />
            </div>
        </div>
    );
}

export default Billing;